// Selección de país para el mapa de regiones de un reporte de fidelización.
//
// El módulo es multi-tenant (cada `configId` es un cliente). Por defecto los
// reportes son de Ecuador (provincias + inset Galápagos). El reporte 7 es un
// cliente de Perú, cuyo mapa se dibuja por departamentos y sin inset.
//
// Cuando haya más clientes de otro país conviene mover esto a un campo `pais`
// en la configuración; por ahora se resuelve por id.

const CONFIG_IDS_PERU = new Set<number>([7])

export function esPeru(configId: number): boolean {
  return CONFIG_IDS_PERU.has(configId)
}

/** URL del geojson a usar según el país del reporte. */
export function GEOJSON_PAIS(configId: number): string {
  return esPeru(configId)
    ? '/geo/peru-departamentos.geojson'
    : '/geo/ecuador-provincias.geojson'
}

/**
 * URL del geojson de distritos (nivel inferior, campo `ciudad`). Solo disponible
 * para Perú (config 7); en el resto devuelve `null` (no hay geometría de distrito).
 */
export function GEOJSON_DISTRITOS(configId: number): string | null {
  return esPeru(configId) ? '/geo/peru-distritos.geojson' : null
}

/** Nombre de la región usado como inset. Perú no tiene inset. */
export function insetPais(configId: number): string | null {
  return esPeru(configId) ? null : 'galapagos'
}

/**
 * Término de división administrativa para títulos/labels según el país del
 * reporte. Ecuador → "provincia"; Perú (config 7) → "departamento". En ambos el
 * nivel inferior (campo `ciudad`) se presenta como "distrito".
 */
export function regionTermino(configId: number, plural = false): string {
  if (esPeru(configId)) return plural ? 'departamentos' : 'departamento'
  return plural ? 'provincias' : 'provincia'
}

/**
 * Término para "factura" según el país del reporte. Perú (config 7) →
 * "comprobante de pago"; el resto → "factura". Respeta plural y capitalización.
 * Usar SOLO en textos visibles (labels/títulos/headers), nunca en identificadores
 * de código, params de API ni nombres de archivo.
 */
export function terminoComprobante(
  configId: number,
  opts?: { plural?: boolean; capitalize?: boolean },
): string {
  const plural = opts?.plural ?? false
  let t = esPeru(configId)
    ? plural ? 'comprobantes de pago' : 'comprobante de pago'
    : plural ? 'facturas' : 'factura'
  if (opts?.capitalize) t = t.charAt(0).toUpperCase() + t.slice(1)
  return t
}

/** Igual que `regionTermino` pero con la inicial en mayúscula (labels/columnas). */
export function regionTerminoCap(configId: number, plural = false): string {
  const t = regionTermino(configId, plural)
  return t.charAt(0).toUpperCase() + t.slice(1)
}

/**
 * Locale para formateo de números del reporte. Perú (config 7) → 'es-PE'
 * (miles con coma, decimales con punto: "1,234.50"); resto → 'es-EC'.
 */
export function localeNumero(configId: number): string {
  return esPeru(configId) ? 'es-PE' : 'es-EC'
}

/**
 * Formatea un número con el locale del reporte (ej. "1,234.50" en Perú).
 * Los valores no numéricos se devuelven tal cual (como texto).
 */
export function formatNumero(
  value: number | string | null | undefined,
  configId: number,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  if (Number.isNaN(n)) return String(value)
  return new Intl.NumberFormat(localeNumero(configId), opts).format(n)
}

/** Moneda del reporte según el país. Perú → soles ("S./"); resto → dólares (USD). */
export function monedaPais(configId: number): {
  currency: string
  locale: string
  simbolo: string
} {
  return esPeru(configId)
    ? { currency: 'PEN', locale: 'es-PE', simbolo: 'S./' }
    : { currency: 'USD', locale: 'es-EC', simbolo: '$' }
}

/**
 * Formatea un monto en la moneda del reporte (ej. "S./ 1,234.00" en Perú,
 * "$1,234.00" en el resto). Devuelve "—" para valores nulos o no numéricos.
 *
 * En Perú se antepone el símbolo "S./" manualmente porque `Intl` con la moneda
 * PEN emite "S/" (sin punto), y el requerimiento del cliente es "S./".
 */
export function formatMoneda(
  value: number | string | null | undefined,
  configId: number,
  opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  if (Number.isNaN(n)) return '—'
  const { currency, locale, simbolo } = monedaPais(configId)
  const max = opts?.maximumFractionDigits ?? 2
  const min = opts?.minimumFractionDigits ?? Math.min(2, max)
  if (esPeru(configId)) {
    const num = new Intl.NumberFormat(locale, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(n)
    return `${simbolo} ${num}`
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(n)
}
